import React, { useEffect } from 'react'
import { StickyNote, Plus, Search, TrendingUp } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useNotes } from '../contexts/NotesContext'

/**
 * Dashboard Page Component
 * 
 * Main landing page for authenticated users showing overview and quick actions.
 */
const DashboardPage = () => {
  const { pagination, fetchNotes } = useNotes()

  // Fetch notes to get the total count
  useEffect(() => {
    fetchNotes()
  }, [])

  // Determine button text based on total notes count
  const getCreateButtonText = () => {
    return pagination.totalElements === 0 ? "Create Your First Note" : "Create new note"
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Welcome back! Here's an overview of your notes.
        </p>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <Link
          to="/notes/new"
          className="card hover:shadow-md transition-shadow"
        >
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Plus className="h-8 w-8 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    New Note
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    Create
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </Link>

        <Link
          to="/notes"
          className="card hover:shadow-md transition-shadow"
        >
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <StickyNote className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    All Notes
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    View
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </Link>

        <Link
          to="/notes?search="
          className="card hover:shadow-md transition-shadow"
        >
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Search className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Search
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    Find
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </Link>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TrendingUp className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Statistics
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    Coming Soon
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Welcome Message */}
      <div className="card">
        <div className="card-body">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
            Welcome to NoteTaker
          </h3>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            Start organizing your thoughts and ideas with our intuitive note-taking platform. 
            Create, edit, and organize your notes with ease.
          </p>
          <div className="flex space-x-4">
            <Link to="/notes/new" className="btn-primary">
              {getCreateButtonText()}
            </Link>
            <Link to="/notes" className="btn-outline">
              Browse Notes
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DashboardPage 